import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { RowSelectionState } from "@tanstack/react-table";
import {
    useCallback,
    useMemo,
    useRef,
    useState,
    type SetStateAction,
} from "react";
import { endOfDay } from "date-fns";
import {
    Bill,
    BillLineItem,
    BillSource,
    BillViewOptionGroup,
} from "@common/types/bill";
import {
    DateOnlyDateRange,
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsClassesEnabled } from "@hooks/useIsClassesEnabled";
import { useLineItemsBulkActions } from "@hooks/useLineItemsBulkActions";
import { useEntities } from "@hooks/useEntities";
import { useBulkAccrualAccountEntityId } from "@hooks/useBulkAccrualAccountEntityId";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useConstant } from "@hooks/useConstant";
import {
    BillColumn,
    BillLineItemColumn,
    BillLineItemView,
    BillsByVendorView,
    BillView,
    BillLineItemViewSchema,
    BillsByVendorViewSchema,
    BillViewSchema,
} from "@common/types/views/billViews";
import { ensureRequiredColumns } from "@hooks/view/ensureRequiredColumns";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import {
    getBillcomNewBillUrl,
    getRampUrlForInvoiceCreation,
    isBillProviderAccount,
} from "@helpers/bills";
import {
    FinancialAccount,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import { useScope } from "@state/scopeContext.lib";
import {
    useViewChangeHandler,
    useViewSelectHandler,
} from "@hooks/view/useViewChangeHandler";
import { useOverlays } from "contexts/OverlayContext";
import { useBulkUpdateBillLineItems } from "@api/bill.api";
import { useHasAnyAccrualLedger } from "@api/ledger.api";
import { useMultiLedgerColumns } from "@hooks/useMultiLedgerColumns";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    defaultBillStatuses,
    FiltersChangeFn,
} from "../pageHeader/filters/lib";
import { useBillsView } from "../../contexts/BillsViewContext";
import { CreateAccountHandle } from "../accounts/AddAccounts/lib";
import {
    addPayablesToDefaultBillLineItemsView,
    defaultBillLineItemsView,
    defaultBillsByVendorView,
    defaultBillsView,
} from "./views/defaultBillViews";
import { createBillLineItemsViewConfig } from "./views/billLineItemsView/billLineItemsViewConfig";
import { useBillDetailsHandlers } from "./useBillDetailsHandlers";
import {
    useBuiltInBillViews,
    useBuiltInBillLineItemViews,
    useBuiltInBillsByVendorViews,
} from "./views/BuiltInBillViews";

export const useBillPage = (billAccounts: FinancialAccount[]) => {
    const { error } = useToaster();
    const [selectedBill, setSelectedBill] = useState<Bill>();
    const [cachedBillTableView, setCachedBillTableView] =
        useWorkspaceUserCache<BillView>(
            WORKSPACE_USER_CACHE_KEYS.BILLS_PAGE_BILL_VIEW_CACHE_KEY,
            BillViewSchema,
        );
    const [cachedBillLineItemsTableView, setCachedBillLineItemsTableView] =
        useWorkspaceUserCache<BillLineItemView>(
            WORKSPACE_USER_CACHE_KEYS.BILLS_PAGE_LINE_ITEMS_VIEW_CACHE_KEY,
            BillLineItemViewSchema,
        );
    const [cachedBillsByVendorTableView, setCachedBillsByVendorTableView] =
        useWorkspaceUserCache<BillsByVendorView>(
            WORKSPACE_USER_CACHE_KEYS.BILLS_PAGE_BY_VENDOR_VIEW_CACHE_KEY,
            BillsByVendorViewSchema,
        );
    const [billTableView, setBillTableView] = useState<BillView>(() =>
        cachedBillTableView
            ? {
                  ...cachedBillTableView,
                  columns: ensureRequiredColumns(
                      cachedBillTableView.columns,
                      defaultBillsView.columns,
                      [BillColumn.LOCK],
                  ),
              }
            : defaultBillsView,
    );
    const [billLineItemsTableViewState, setBillLineItemsTableViewState] =
        useState<BillLineItemView>(() =>
            cachedBillLineItemsTableView
                ? {
                      ...cachedBillLineItemsTableView,
                      columns: ensureRequiredColumns(
                          cachedBillLineItemsTableView.columns,
                          defaultBillLineItemsView.columns,
                          [
                              BillLineItemColumn.LOCK,
                              BillLineItemColumn.ACCRUAL_ACCOUNT,
                          ],
                      ),
                  }
                : defaultBillLineItemsView,
        );
    const [hasCustomizedBillLineItemsView, setHasCustomizedBillLineItemsView] =
        useState(() => cachedBillLineItemsTableView !== undefined);
    const setBillLineItemsTableView = useCallback(
        (action: SetStateAction<BillLineItemView>) => {
            setHasCustomizedBillLineItemsView(true);
            setBillLineItemsTableViewState(action);
        },
        [],
    );
    const [billsByVendorTableView, setBillsByVendorTableView] =
        useState<BillsByVendorView>(
            cachedBillsByVendorTableView ?? defaultBillsByVendorView,
        );

    const {
        manualAccrualCreation: isManualAccrualCreationEnabled,
        schedules: isSchedulesFlagEnabled,
        schedulesExpenses: isSchedulesExpensesFlagEnabled,
    } = useTypedFlags();
    const isBillSchedulesEnabled =
        isSchedulesFlagEnabled && isSchedulesExpensesFlagEnabled;
    const { openOverlay } = useOverlays();
    const entities = useEntities({ onlyBusiness: true });
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const hasAnyAccrualLedgers = useHasAnyAccrualLedger(entityIds);
    const hasVisibleAccrualLedgerColumn = hasAnyAccrualLedgers;
    const billLineItemsTableView = useMemo(
        () =>
            hasCustomizedBillLineItemsView || !hasVisibleAccrualLedgerColumn
                ? billLineItemsTableViewState
                : addPayablesToDefaultBillLineItemsView(
                      billLineItemsTableViewState,
                  ),
        [
            hasCustomizedBillLineItemsView,
            hasVisibleAccrualLedgerColumn,
            billLineItemsTableViewState,
        ],
    );
    const multiLedgerLedgers = useMultiLedgerColumns(entityIds, {
        basis: AccountingLedgerBasis.accruals,
        uniqueByBasisAndName: true,
    });
    const isClassesEnabled = useIsClassesEnabled();
    const billLineItemsViewConfig = useMemo(
        () =>
            createBillLineItemsViewConfig({
                hasAnyAccrualLedgers: hasVisibleAccrualLedgerColumn,
                isClassesEnabled,
                isSchedulesEnabled: isBillSchedulesEnabled,
                multiLedgerLedgers,
            }),
        [
            hasVisibleAccrualLedgerColumn,
            isClassesEnabled,
            isBillSchedulesEnabled,
            multiLedgerLedgers,
        ],
    );
    const { workspaceId } = useScope();
    const bulkUpdateBillLineItems = useBulkUpdateBillLineItems();
    const {
        handleUpdateMemo: handleUpdateBillMemo,
        handleUpdateStatus: handleUpdateBillStatus,
    } = useBillDetailsHandlers({
        workspaceId,
        bill: selectedBill,
        setBill: setSelectedBill,
    });

    const viewOptions = useMemo(
        () => [
            BillViewOptionGroup.BILL,
            BillViewOptionGroup.COUNTERPARTY,
            BillViewOptionGroup.LINE_ITEMS,
        ],
        [],
    );

    const builtInBillViews = useBuiltInBillViews();
    const builtInBillLineItemViews = useBuiltInBillLineItemViews(
        hasVisibleAccrualLedgerColumn,
    );
    const builtInBillsByVendorViews = useBuiltInBillsByVendorViews();

    const {
        filters,
        setFilters,
        viewOption,
        setViewOption,
        billStats,
        initialIds,
    } = useBillsView();

    const {
        selectedLineItems: selectedBillLineItems,
        hasSelectedByCurrentFilters,
        selectedCount: selectedBillLineItemsCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsPayableSelected,
    } = useLineItemsBulkActions<BillLineItem, typeof filters>({
        workspaceId,
        filters,
        onBulkUpdate: bulkUpdateBillLineItems.mutateAsync,
        onError: error,
    });

    const getBillFinancialAccountId = useCallback(
        (lineItem: BillLineItem) => lineItem.bill?.financialAccountId,
        [],
    );
    const getBillEntityId = useCallback(
        (lineItem: BillLineItem) => lineItem.bill?.entityId,
        [],
    );
    const bulkAccrualAccountEntityId = useBulkAccrualAccountEntityId({
        filters,
        hasSelectedByCurrentFilters,
        selectedLineItems: selectedBillLineItems,
        getFinancialAccountId: getBillFinancialAccountId,
        getEntityId: getBillEntityId,
    });

    const [billRowSelection, setBillRowSelection] = useState<RowSelectionState>(
        {},
    );
    const [editBills, setEditBills] = useState<Bill[] | null>(null);

    const clearBillSelection = useCallback(() => {
        setBillRowSelection({});
    }, []);

    const onBillClick = useCallback(
        (bill?: Bill) => {
            setSelectedBill((previousBill) => {
                if (bill && previousBill?.id === bill.id) {
                    return undefined;
                }
                return bill;
            });
        },
        [setSelectedBill],
    );

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const newFilters = omitBy(
                { ...filters, ...value },
                (v) => v === null || v === undefined,
            );

            if (isEqual(newFilters, filters)) {
                return;
            }

            setFilters(newFilters);
            setSelectedBill(undefined);
            clearLineItemsSelection();
            clearBillSelection();
        },
        [clearLineItemsSelection, clearBillSelection, filters, setFilters],
    );

    const handleDateRangeChange = useCallback(
        (value: DateOnlyDateRange, isAllTime?: boolean) => {
            const endOfToday = endOfDay(new Date());
            if (isAllTime) {
                handleFiltersChange({ start: undefined, end: endOfToday });
            } else {
                const localRange = toLocalDateRange(value);
                handleFiltersChange({
                    start: localRange.start,
                    end: localRange.end,
                });
            }
        },
        [handleFiltersChange],
    );

    const today = useConstant(() => new Date());
    const defaultFilters = useMemo(() => {
        const last90DaysPreset = DATE_RANGE_PRESETS_BY_ID.last_90_days;
        return {
            externalBillStatuses: defaultBillStatuses,
            start: last90DaysPreset.getStart(today),
            end: last90DaysPreset.getEnd(today),
        };
    }, [today]);

    const dateRange = useMemo(() => {
        if (filters?.start === undefined && filters?.end === undefined) {
            return toDateOnlyDateRange({
                start: defaultFilters.start,
                end: defaultFilters.end,
            });
        }

        // All time is stored as "no start, has end" (see handleDateRangeChange).
        // Do not compare `end` to today — that breaks across midnight / persisted state.
        if (filters?.start === undefined && filters?.end !== undefined) {
            return {
                start: undefined,
                end: undefined,
            };
        }

        return toDateOnlyDateRange({
            start: filters?.start,
            end: filters?.end,
        });
    }, [filters, defaultFilters]);

    const handleViewOptionChange = useCallback(
        (value: BillViewOptionGroup) => {
            setViewOption({ groupBy: value });
            clearLineItemsSelection();
            clearBillSelection();

            if (value === BillViewOptionGroup.COUNTERPARTY) {
                setSelectedBill(undefined);
            }
        },
        [
            clearLineItemsSelection,
            clearBillSelection,
            setViewOption,
            setSelectedBill,
        ],
    );

    const handleLineItemsEditClick = useCallback(() => {
        const lineItems = Object.values(selectedBillLineItems);
        const uniqueBills = new Map<string, Bill>();
        for (const lineItem of lineItems) {
            if (lineItem.bill) {
                uniqueBills.set(lineItem.bill.id, lineItem.bill);
            }
        }
        setEditBills(Array.from(uniqueBills.values()));
    }, [selectedBillLineItems]);

    const isLineItemsEditAvailable = useMemo(() => {
        if (!isManualAccrualCreationEnabled || hasSelectedByCurrentFilters) {
            return false;
        }
        const lineItems = Object.values(selectedBillLineItems);
        if (lineItems.length === 0) {
            return false;
        }
        return lineItems.every((li) => li.bill?.source === BillSource.IMPORTER);
    }, [
        isManualAccrualCreationEnabled,
        hasSelectedByCurrentFilters,
        selectedBillLineItems,
    ]);

    const handleBillViewChange = useViewChangeHandler({
        setView: setBillTableView,
        setCachedView: setCachedBillTableView,
    });

    const handleBillViewSelect = useViewSelectHandler({
        setView: setBillTableView,
        setCachedView: setCachedBillTableView,
        setFilters,
        defaultFilters,
    });

    const handleBillLineItemsViewChange = useViewChangeHandler({
        setView: setBillLineItemsTableView,
        setCachedView: setCachedBillLineItemsTableView,
    });

    const handleBillLineItemsViewSelect = useViewSelectHandler({
        setView: setBillLineItemsTableView,
        setCachedView: setCachedBillLineItemsTableView,
        setFilters,
        defaultFilters,
    });

    const handleBillsByVendorViewChange = useViewChangeHandler({
        setView: setBillsByVendorTableView,
        setCachedView: setCachedBillsByVendorTableView,
    });

    const handleBillsByVendorViewSelect = useViewSelectHandler({
        setView: setBillsByVendorTableView,
        setCachedView: setCachedBillsByVendorTableView,
        setFilters,
        defaultFilters,
    });

    const handleClearFilters = useCallback(() => {
        setFilters(defaultFilters);
        setSelectedBill(undefined);
        clearLineItemsSelection();
        clearBillSelection();
    }, [
        clearBillSelection,
        clearLineItemsSelection,
        defaultFilters,
        setFilters,
    ]);

    const [showAccountSelectModal, setShowAccountSelectModal] = useState(false);

    const rampConnectionRef = useRef<CreateAccountHandle>(null);
    const billcomConnectionRef = useRef<CreateAccountHandle>(null);
    const hasRampAccounts = useMemo(
        () =>
            billAccounts.some(
                (a) =>
                    a.sourceType === FinancialAccountSourceType.RAMP &&
                    isBillProviderAccount(a),
            ),
        [billAccounts],
    );
    const hasBillcomAccounts = useMemo(
        () =>
            billAccounts.some(
                (a) => a.sourceType === FinancialAccountSourceType.BILLCOM,
            ),
        [billAccounts],
    );

    const createBillAccounts = useMemo(
        () =>
            billAccounts.filter(
                (a) =>
                    a.sourceType === FinancialAccountSourceType.RAMP ||
                    a.sourceType === FinancialAccountSourceType.BILLCOM,
            ),
        [billAccounts],
    );

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const getCreateBillUrl = useCallback(
        (account: FinancialAccount): string | undefined => {
            if (account.sourceType === FinancialAccountSourceType.BILLCOM) {
                return getBillcomNewBillUrl();
            }
            return getRampUrlForInvoiceCreation();
        },
        [],
    );

    const onCreateBillcomBill = useCallback(() => {
        // The next step leaves the app for the provider, so the server never
        // gets to refuse it.
        if (refuseIfViewOnly()) {
            return;
        }

        if (!hasBillcomAccounts) {
            billcomConnectionRef.current?.initializeConnection();
            return;
        }

        window.open(getBillcomNewBillUrl(), "_blank", "noopener,noreferrer");
    }, [hasBillcomAccounts, refuseIfViewOnly]);

    const onCreateBill = useCallback(() => {
        // The next step leaves the app for the provider, so the server never
        // gets to refuse it.
        if (refuseIfViewOnly()) {
            return;
        }

        if (createBillAccounts.length === 0) {
            window.open(getRampUrlForInvoiceCreation(), "_blank");
            return;
        }
        if (createBillAccounts.length === 1) {
            const url = getCreateBillUrl(createBillAccounts[0]);
            if (url) {
                window.open(url, "_blank");
            }
            return;
        }
        setShowAccountSelectModal(true);
    }, [createBillAccounts, getCreateBillUrl, refuseIfViewOnly]);

    const onAccountSelect = useCallback(
        (account: FinancialAccount) => {
            setShowAccountSelectModal(false);
            const url = getCreateBillUrl(account);
            if (url) {
                window.open(url, "_blank");
            }
        },
        [getCreateBillUrl],
    );

    return {
        selectedBill,
        setSelectedBill,
        billTableView,
        billLineItemsTableView,
        billsByVendorTableView,
        isManualAccrualCreationEnabled,
        isBillSchedulesEnabled,
        openOverlay,
        hasVisibleAccrualLedgerColumn,
        isClassesEnabled,
        billLineItemsViewConfig,
        bulkUpdateBillLineItems,
        handleUpdateBillMemo,
        handleUpdateBillStatus,
        viewOptions,
        builtInBillViews,
        builtInBillLineItemViews,
        builtInBillsByVendorViews,
        filters,
        setFilters,
        viewOption,
        billStats,
        initialIds,
        selectedBillLineItems,
        hasSelectedByCurrentFilters,
        selectedBillLineItemsCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsPayableSelected,
        bulkAccrualAccountEntityId,
        billRowSelection,
        setBillRowSelection,
        editBills,
        setEditBills,
        clearBillSelection,
        onBillClick,
        handleFiltersChange,
        handleDateRangeChange,
        defaultFilters,
        dateRange,
        handleViewOptionChange,
        handleLineItemsEditClick,
        isLineItemsEditAvailable,
        handleBillViewChange,
        handleBillViewSelect,
        handleBillLineItemsViewChange,
        handleBillLineItemsViewSelect,
        handleBillsByVendorViewChange,
        handleBillsByVendorViewSelect,
        handleClearFilters,
        createBillAccounts,
        showAccountSelectModal,
        setShowAccountSelectModal,
        onAccountSelect,
        onCreateBill,
        onCreateBillcomBill,
        rampConnectionRef,
        billcomConnectionRef,
        hasRampAccounts,
        hasBillcomAccounts,
    };
};
